import { useEffect, useMemo, useState } from 'react'
import { Loader2, Package, Truck } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { useGetOrderTotes, useCheckInTote, useMarkOrderFulfilled } from '../../hooks/backend/scanPage'
import { toast } from '../../lib/shadcn/sonner'
import { fulfillmentSyncWarning } from '../../utils/syncResults'

export interface BulkDeliverOrder {
  id: string
  customer: string
}

interface Tote {
  id: string
  customer: string | null
}

interface CustomerGroup {
  customer: string
  orderIds: string[]
  totes: Tote[]
}

interface Props {
  orders: BulkDeliverOrder[] | null
  onOpenChange: (open: boolean) => void
  // Called after the run with the orders that were marked delivered.
  onDone: (deliveredIds: string[]) => void
}

// Marks several delivery orders delivered at once. Totes are checked out per
// customer, so the returned-tote list is grouped by customer and only the
// totes ticked here are checked in.
export default function BulkDeliverDialog({ orders, onOpenChange, onDone }: Props) {
  const getOrderTotes = useGetOrderTotes()
  const checkInTote = useCheckInTote()
  const markOrderFulfilled = useMarkOrderFulfilled()

  const [groups, setGroups] = useState<CustomerGroup[]>([])
  const [selectedTotes, setSelectedTotes] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState('')

  const open = orders !== null && orders.length > 0

  useEffect(() => {
    if (!orders || orders.length === 0) return
    let cancelled = false
    setLoading(true)
    setGroups([])
    setSelectedTotes(new Set())

    // One tote lookup per customer: totes belong to the customer, not the order.
    const byCustomer = new Map<string, CustomerGroup>()
    for (const o of orders) {
      const key = o.customer || `order ${o.id}`
      const g = byCustomer.get(key) ?? { customer: o.customer || '—', orderIds: [], totes: [] }
      g.orderIds.push(o.id)
      byCustomer.set(key, g)
    }
    const list = Array.from(byCustomer.values())

    void Promise.all(
      list.map(async g => {
        try {
          const data = (await getOrderTotes.trigger({ order_id: g.orderIds[0] })) as Tote[] | undefined
          const seen = new Set<string>()
          g.totes = (data ?? []).filter(t => (seen.has(t.id) ? false : (seen.add(t.id), true)))
        } catch {
          g.totes = []
        }
        return g
      })
    )
      .then(loaded => {
        if (!cancelled) setGroups(loaded)
      })
      .catch(() => toast.error('Failed to load checked-out totes'))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders])

  const allToteIds = useMemo(() => groups.flatMap(g => g.totes.map(t => t.id)), [groups])

  const toggleTote = (id: string, checked: boolean) => {
    setSelectedTotes(prev => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const toggleGroup = (g: CustomerGroup, checked: boolean) => {
    setSelectedTotes(prev => {
      const next = new Set(prev)
      for (const t of g.totes) {
        if (checked) next.add(t.id)
        else next.delete(t.id)
      }
      return next
    })
  }

  const handleConfirm = async () => {
    if (!orders) return
    setRunning(true)
    const toteFailures: string[] = []
    const orderFailures: { id: string; message: string }[] = []
    const delivered: string[] = []
    const syncWarnings: string[] = []

    try {
      const toteIds = Array.from(selectedTotes)
      for (let i = 0; i < toteIds.length; i++) {
        const id = toteIds[i]!
        setProgress(`Checking in tote ${i + 1} of ${toteIds.length}…`)
        try {
          await checkInTote.trigger({ tote_id: id })
        } catch {
          toteFailures.push(id)
        }
      }

      // One at a time: each delivery also updates the B2B order stage.
      for (let i = 0; i < orders.length; i++) {
        const o = orders[i]!
        setProgress(`Marking order ${i + 1} of ${orders.length} delivered…`)
        try {
          const result = await markOrderFulfilled.trigger({ order_id: o.id, status: 'delivered' })
          delivered.push(o.id)
          const warning = fulfillmentSyncWarning((result as { sync_results?: unknown } | undefined)?.sync_results, o.id)
          if (warning) syncWarnings.push(warning)
        } catch (err) {
          orderFailures.push({ id: o.id, message: err instanceof Error ? err.message : 'failed' })
        }
      }
    } finally {
      setRunning(false)
      setProgress('')
    }

    if (delivered.length > 0) {
      toast.success(`${delivered.length} order${delivered.length === 1 ? '' : 's'} marked delivered`)
    }
    if (orderFailures.length > 0) {
      toast.error(
        `Couldn't mark ${orderFailures.map(f => f.id).join(', ')} delivered: ${orderFailures[0]!.message}. They stay selected so you can retry.`
      )
    }
    for (const warning of syncWarnings) toast.warning(warning)
    if (toteFailures.length > 0) {
      toast.error(`Couldn't check in tote${toteFailures.length === 1 ? '' : 's'} ${toteFailures.join(', ')}`)
    }

    // Failed orders stay selected on the page, so closing lets the user retry
    // just those without re-sending the ones that went through.
    onDone(delivered)
    onOpenChange(false)
  }

  const count = orders?.length ?? 0

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && !running) onOpenChange(false)
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Mark {count} order{count === 1 ? '' : 's'} delivered
          </DialogTitle>
          <DialogDescription>
            Tick any totes that came back on this route, then confirm. Unticked totes stay checked out.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-foreground">
              Returned totes {loading ? '' : `(${selectedTotes.size} of ${allToteIds.length})`}
            </span>
            {allToteIds.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                disabled={running}
                onClick={() =>
                  setSelectedTotes(
                    selectedTotes.size === allToteIds.length ? new Set() : new Set(allToteIds)
                  )
                }
              >
                {selectedTotes.size === allToteIds.length ? 'Clear All' : 'Select All'}
              </Button>
            )}
          </div>

          <div className="max-h-[320px] overflow-y-auto rounded-md border divide-y">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : (
              groups.map(g => {
                const groupSelected = g.totes.filter(t => selectedTotes.has(t.id)).length
                return (
                  <div key={g.customer + g.orderIds.join(',')} className="px-3 py-2 flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                        {g.totes.length > 0 && (
                          <Checkbox
                            checked={
                              groupSelected === g.totes.length
                                ? true
                                : groupSelected > 0
                                  ? 'indeterminate'
                                  : false
                            }
                            disabled={running}
                            onCheckedChange={v => toggleGroup(g, !!v)}
                            aria-label={`Select all totes for ${g.customer}`}
                          />
                        )}
                        {g.customer}
                      </label>
                      <span className="text-xs text-muted-foreground font-mono">{g.orderIds.join(', ')}</span>
                    </div>
                    {g.totes.length === 0 ? (
                      <span className="text-xs text-muted-foreground pl-6">No checked-out totes</span>
                    ) : (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 pl-6">
                        {g.totes.map(t => (
                          <label key={t.id} className="flex items-center gap-2 text-sm text-foreground">
                            <Checkbox
                              checked={selectedTotes.has(t.id)}
                              disabled={running}
                              onCheckedChange={v => toggleTote(t.id, !!v)}
                              aria-label={`Tote ${t.id}`}
                            />
                            <Package className="w-3 h-3 text-muted-foreground" />
                            <span className="font-mono">{t.id}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
          {progress && <p className="text-xs text-muted-foreground">{progress}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running}>
            Cancel
          </Button>
          <Button onClick={() => void handleConfirm()} disabled={running || loading}>
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Truck className="w-4 h-4 mr-1" />}
            Mark {count} Delivered
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
